import { Link } from 'react-router-dom'
import LegalLayout from './LegalLayout.jsx'

const UPDATED_AT = '21 de outubro de 2026'

const SECTIONS = [
  { id: 'introducao', title: '1. Introdução' },
  { id: 'dados-coletados', title: '2. Dados que coletamos' },
  { id: 'google-ads', title: '3. Integração com o Google' },
  { id: 'meta', title: '4. Integração com a Meta' },
  { id: 'uso-dados', title: '5. Como usamos os dados' },
  { id: 'compartilhamento', title: '6. Compartilhamento de dados' },
  { id: 'base-legal', title: '7. Finalidade e base legal' },
  { id: 'armazenamento', title: '8. Armazenamento e segurança' },
  { id: 'retencao', title: '9. Retenção de dados' },
  { id: 'direitos', title: '10. Seus direitos' },
  { id: 'revogacao', title: '11. Revogação de acesso' },
  { id: 'exclusao', title: '12. Exclusão de dados' },
  { id: 'alteracoes', title: '13. Alterações nesta política' },
  { id: 'contato', title: '14. Contato' },
]

export default function Privacy() {
  return (
    <LegalLayout
      eyebrow="Política de Privacidade"
      title="Política de Privacidade"
      updatedAt={UPDATED_AT}
      sections={SECTIONS}
    >
      <section id="introducao">
        <h2>
          <span className="legal-num">01</span> Introdução
        </h2>
        <p>
          A <strong>TITAM</strong> ("TITAM", "nós" ou "plataforma") é uma plataforma SaaS de gestão e
          análise de campanhas digitais. Esta Política de Privacidade descreve como coletamos,
          utilizamos, armazenamos e protegemos as informações dos usuários ("você") ao utilizar nossos
          serviços, incluindo as integrações com contas do Google (Google Ads e Google Calendar) e da Meta (Facebook, Instagram e WhatsApp).
        </p>
        <p>
          Ao criar uma conta e utilizar a TITAM, você concorda com as práticas descritas nesta
          política. Recomendamos a leitura completa deste documento.
        </p>
      </section>

      <section id="dados-coletados">
        <h2>
          <span className="legal-num">02</span> Dados que coletamos
        </h2>
        <p>Coletamos apenas os dados necessários para autenticação e para a prestação dos serviços:</p>
        <ul>
          <li>
            <strong>Dados de cadastro:</strong> nome, endereço de e-mail e informações da organização
            associada à sua conta.
          </li>
          <li>
            <strong>Dados de autenticação:</strong> credenciais e tokens de acesso necessários para
            estabelecer e manter a conexão com serviços de terceiros autorizados por você.
          </li>
          <li>
            <strong>Dados de campanhas:</strong> métricas, campanhas, grupos de anúncios e anúncios
            obtidos das suas contas do Google Ads, utilizados exclusivamente para exibição e análise
            dentro da plataforma.
          </li>
          <li>
            <strong>Dados de agenda:</strong> quando você conecta o Google Calendar, os eventos do
            calendário conectado — título, descrição, local, link da reunião, horários e o
            identificador do evento —, usados exclusivamente para manter a sua Agenda comercial
            sincronizada.
          </li>
          <li>
            <strong>Dados da Meta:</strong> Páginas e contas de anúncio que você autoriza, métricas de
            campanhas, leads recebidos por formulários de Lead Ads (nome, e-mail, telefone e demais
            respostas do formulário), mensagens e contatos de conversas iniciadas por anúncios
            Click-to-WhatsApp, e eventos enviados pela API de Conversões.
          </li>
        </ul>
        <div className="legal-callout">
          <p>
            A TITAM coleta e armazena <strong>apenas os dados estritamente necessários</strong> para
            autenticação e para a exibição de métricas e desempenho das campanhas. Não coletamos dados
            sensíveis que não sejam essenciais à operação dos serviços.
          </p>
        </div>
      </section>

      <section id="google-ads">
        <h2>
          <span className="legal-num">03</span> Integração com o Google
        </h2>
        <p>
          A TITAM permite que você conecte suas contas do Google Ads por meio do protocolo{' '}
          <strong>Google OAuth 2.0</strong>. Durante a autorização, você concede à plataforma acesso
          de leitura às informações da sua conta do Google Ads necessárias para exibir métricas,
          campanhas, grupos de anúncios e anúncios (escopo <code>adwords</code>).
        </p>
        <p>
          A plataforma também permite conectar o seu <strong>Google Calendar</strong> à Agenda
          comercial, pelo mesmo protocolo OAuth 2.0 e com o escopo{' '}
          <code>calendar.events</code>. Esse acesso é usado exclusivamente para manter a agenda em
          dia nos dois sentidos: os compromissos criados na TITAM viram eventos no seu calendário e
          os eventos do seu calendário aparecem na Agenda. Guardamos apenas o necessário para essa
          sincronização — identificador do evento, do calendário e a data da última sincronização —
          além dos dados do próprio compromisso (título, descrição, local, link da reunião e
          horários). Não lemos, indexamos nem usamos o conteúdo do seu calendário para qualquer
          outra finalidade, e nenhum dado do calendário é usado para treinar modelos de IA.
        </p>
        <p>
          O uso e a transferência de informações recebidas das APIs do Google pela TITAM obedecem à{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Política de Dados do Usuário dos Serviços de API do Google
          </a>
          , incluindo os requisitos de Uso Limitado (Limited Use).
        </p>
        <ul>
          <li>Utilizamos os dados do Google apenas para fornecer as funcionalidades visíveis a você.</li>
          <li>Não usamos os dados do Google para publicidade direcionada própria.</li>
          <li>Não transferimos os dados do Google a terceiros, exceto conforme descrito nesta política.</li>
          <li>Não permitimos que humanos leiam os dados, salvo com seu consentimento explícito, por motivos de segurança, para cumprir a lei, ou quando os dados forem agregados e anonimizados.</li>
        </ul>
      </section>

      <section id="meta">
        <h2>
          <span className="legal-num">04</span> Integração com a Meta
        </h2>
        <p>
          A TITAM permite que você conecte, por meio do login da Meta (OAuth), as Páginas e contas de
          anúncio do Facebook e do Instagram que você administra. Solicitamos as permissões{' '}
          <code>ads_read</code>, <code>business_management</code>, <code>public_profile</code>,{' '}
          <code>email</code>, <code>pages_show_list</code>, <code>pages_read_engagement</code>,{' '}
          <code>pages_manage_metadata</code> e <code>leads_retrieval</code>, cada uma usada apenas para
          o fim descrito abaixo:
        </p>
        <ul>
          <li>
            <strong>Contas de anúncio e Business Managers:</strong> listar suas contas e exibir
            métricas, campanhas, conjuntos e anúncios.
          </li>
          <li>
            <strong>Lead Ads:</strong> listar suas Páginas, assinar o recebimento de novos leads e ler
            os dados dos formulários preenchidos, criando o lead no CRM da sua organização.
          </li>
          <li>
            <strong>Click-to-WhatsApp:</strong> associar conversas iniciadas por anúncios ao anúncio de
            origem e ao lead correspondente.
          </li>
          <li>
            <strong>API de Conversões:</strong> enviar à Meta eventos de conversão (por exemplo, lead
            qualificado ou venda) gerados no CRM, para otimizar suas campanhas. Esses eventos podem
            conter dados de contato em formato com hash, conforme as regras da Meta.
          </li>
        </ul>
        <p>
          Os tokens de acesso da Meta são armazenados com acesso restrito e usados somente para essas
          finalidades. Não usamos os dados da Meta para publicidade própria, não os vendemos e não os
          transferimos a corretores de dados. O tratamento segue os{' '}
          <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer">
            Termos da Plataforma Meta
          </a>{' '}
          e as Políticas para Desenvolvedores.
        </p>
      </section>

      <section id="uso-dados">
        <h2>
          <span className="legal-num">05</span> Como usamos os dados
        </h2>
        <p>Os dados coletados são utilizados exclusivamente para:</p>
        <ul>
          <li>Autenticar e identificar você na plataforma;</li>
          <li>Estabelecer e manter a conexão com suas contas do Google e da Meta;</li>
          <li>Receber leads e conversas de anúncios e registrá-los no CRM da sua organização;</li>
          <li>Enviar eventos de conversão à Meta, por solicitação sua;</li>
          <li>Exibir métricas, campanhas, grupos de anúncios e anúncios em painéis e relatórios;</li>
          <li>Gerar análises e insights sobre o desempenho das suas campanhas;</li>
          <li>Operar, manter e melhorar os serviços oferecidos pela TITAM.</li>
        </ul>
        <p>
          Os dados são utilizados <strong>exclusivamente para fornecer os serviços da plataforma</strong> e
          não são empregados para nenhuma finalidade incompatível com este propósito.
        </p>
      </section>

      <section id="compartilhamento">
        <h2>
          <span className="legal-num">06</span> Compartilhamento de dados
        </h2>
        <p>
          <strong>A TITAM não vende dados dos usuários para terceiros.</strong> Não comercializamos,
          alugamos ou negociamos suas informações.
        </p>
        <p>
          Podemos compartilhar dados apenas nas situações estritamente necessárias para a operação dos
          serviços, como com provedores de infraestrutura e processamento que atuam em nosso nome sob
          obrigações de confidencialidade, ou quando exigido por lei ou ordem judicial.
        </p>
        <p>
          Compartilhamos dados com a <strong>Meta</strong> e o <strong>Google</strong> apenas na medida
          necessária às integrações que você ativa — por exemplo, o envio de eventos pela API de
          Conversões da Meta e a criação de eventos no seu Google Calendar. Esses serviços tratam os
          dados conforme suas próprias políticas de privacidade.
        </p>
      </section>

      <section id="base-legal">
        <h2>
          <span className="legal-num">07</span> Finalidade e base legal
        </h2>
        <p>
          Tratamos dados pessoais conforme a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 —
          LGPD):
        </p>
        <ul>
          <li>
            <strong>Execução de contrato</strong> (art. 7º, V): cadastro, autenticação e prestação das
            funcionalidades contratadas, incluindo as integrações que você conecta.
          </li>
          <li>
            <strong>Consentimento</strong> (art. 7º, I): autorização concedida nas telas de OAuth do
            Google e da Meta, revogável a qualquer momento.
          </li>
          <li>
            <strong>Legítimo interesse</strong> (art. 7º, IX): segurança, prevenção a fraudes e
            melhoria do serviço.
          </li>
          <li>
            <strong>Cumprimento de obrigação legal</strong> (art. 7º, II): guarda de registros quando
            exigida por lei.
          </li>
        </ul>
        <p>
          Em relação aos dados dos leads e contatos que você registra na TITAM, você (ou sua
          organização) é o controlador e a TITAM atua como operadora, tratando esses dados apenas
          conforme as suas instruções.
        </p>
      </section>

      <section id="armazenamento">
        <h2>
          <span className="legal-num">08</span> Armazenamento e segurança
        </h2>
        <p>
          A TITAM segue boas práticas de segurança para o armazenamento e o processamento dos dados.
          Aplicamos medidas técnicas e organizacionais para proteger as informações contra acesso não
          autorizado, perda, alteração ou divulgação indevida.
        </p>
        <ul>
          <li>Comunicação criptografada (HTTPS/TLS) entre seu navegador e nossos servidores;</li>
          <li>Armazenamento seguro de tokens de autenticação, com acesso restrito;</li>
          <li>Controle de acesso baseado em permissões e princípio do menor privilégio.</li>
        </ul>
      </section>

      <section id="retencao">
        <h2>
          <span className="legal-num">09</span> Retenção de dados
        </h2>
        <p>
          Mantemos seus dados apenas pelo tempo necessário para fornecer os serviços ou conforme exigido
          por obrigações legais. Quando você revoga o acesso ou solicita a exclusão da conta, os dados
          associados são removidos ou anonimizados dentro de prazos razoáveis, ressalvadas as obrigações
          legais de retenção. Ao desconectar uma integração, o token de acesso correspondente é
          descartado; os leads e demais registros já criados no CRM permanecem na sua organização até
          que você os exclua ou solicite a exclusão.
        </p>
      </section>

      <section id="direitos">
        <h2>
          <span className="legal-num">10</span> Seus direitos
        </h2>
        <p>Você tem o direito de:</p>
        <ul>
          <li>Acessar os dados pessoais que mantemos sobre você;</li>
          <li>Solicitar a correção de dados incompletos ou imprecisos;</li>
          <li>Solicitar a exclusão dos seus dados, sujeito às obrigações legais aplicáveis;</li>
          <li>Revogar o consentimento e desconectar integrações a qualquer momento;</li>
          <li>Obter informações sobre o compartilhamento dos seus dados e a portabilidade deles.</li>
        </ul>
      </section>

      <section id="revogacao">
        <h2>
          <span className="legal-num">11</span> Revogação de acesso
        </h2>
        <p>
          Você pode revogar o acesso da TITAM à sua conta da Meta a qualquer momento em{' '}
          <a
            href="https://www.facebook.com/settings?tab=business_tools"
            target="_blank"
            rel="noopener noreferrer"
          >
            Configurações do Facebook → Integrações comerciais
          </a>
          , removendo o app, ou desconectando dentro da plataforma. Da mesma forma, você pode revogar o
          acesso da TITAM à sua conta do Google a qualquer momento, diretamente nas
          configurações de segurança da sua conta Google, em{' '}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
            myaccount.google.com/permissions
          </a>
          . Após a revogação, deixaremos de acessar novos dados da sua conta do Google Ads e de
          sincronizar o seu Google Calendar. Você também pode desconectar o calendário dentro da
          plataforma, em Comercial → Agenda → Calendário conectado → Gerenciar conexão: nesse caso
          revogamos o token no Google e a sincronização para, sem apagar os compromissos já
          existentes nem no TITAM nem no seu calendário.
        </p>
      </section>

      <section id="exclusao">
        <h2>
          <span className="legal-num">12</span> Exclusão de dados
        </h2>
        <p>
          Você pode solicitar a exclusão dos seus dados a qualquer momento, inclusive os obtidos por
          meio das integrações com a Meta e o Google:
        </p>
        <ul>
          <li>
            <strong>Pela plataforma:</strong> em Configurações → Meu perfil → Excluir conta, para
            remover permanentemente sua conta e acessos.
          </li>
          <li>
            <strong>Por e-mail:</strong> escreva para{' '}
            <a href="mailto:deebsnow.io@gmail.com">deebsnow.io@gmail.com</a> a partir do e-mail
            cadastrado, e atenderemos a solicitação em até 30 dias.
          </li>
        </ul>
        <p>
          Veja o passo a passo completo, inclusive para quem removeu o app pelo Facebook, em{' '}
          <Link to="/data-deletion">instruções de exclusão de dados</Link>.
        </p>
      </section>

      <section id="alteracoes">
        <h2>
          <span className="legal-num">13</span> Alterações nesta política
        </h2>
        <p>
          Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossos
          serviços ou na legislação aplicável. A data da última atualização é indicada no topo deste
          documento. Recomendamos a revisão periódica desta página.
        </p>
      </section>

      <section id="contato">
        <h2>
          <span className="legal-num">14</span> Contato
        </h2>
        <p>
          Em caso de dúvidas sobre esta Política de Privacidade ou sobre o tratamento dos seus dados,
          entre em contato:
        </p>
        <div className="legal-contact">
          <div className="legal-contact-row">
            <strong>E-mail:</strong> <a href="mailto:deebsnow.io@gmail.com">deebsnow.io@gmail.com</a>
          </div>
          <div className="legal-contact-row">
            <strong>Encarregado de dados (DPO):</strong> mesmo canal de contato acima
          </div>
          <div className="legal-contact-row">
            <strong>Plataforma:</strong> TITAM — Gestão e análise de campanhas digitais
          </div>
        </div>
      </section>
    </LegalLayout>
  )
}
